<template>
  <div class="text-center md:text-left" data-testid="footer-language-currency">
    <p
      class="text-base mb-0 font-bold text-mouse uppercase"
      role="heading"
      aria-level="2"
      data-testid="footer-language-currency-title"
    >
      {{ t('general/footer/block/locales/title') }}
    </p>
    <a
      class="inline-flex cursor-pointer items-center justify-center font-normal tracking-normal text-mouse no-underline transition-colors hover:text-white md:mt-4 md:justify-start"
      data-testid="footer-language-currency-selector"
      @click="isOpen = true"
    >
      <fa-icon class="mr-2 size-4 text-rhino" :icon="faLocationDot" />
      <span data-testid="footer-language-current">{{
        currentDisplayLanguage
      }}</span>
      /
      <span data-testid="footer-currency-current">{{ currentCurrency }}</span>
    </a>

    <!-- Slide-in panel -->
    <teleport to="body">
      <transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-300"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isOpen"
          class="fixed inset-0 z-1060 bg-onyx/80"
          aria-hidden="true"
          @click="close"
        />
      </transition>

      <aside
        class="fixed top-0 right-0 z-1060 h-dvh w-full overflow-y-auto bg-linear-to-br from-depth-0 from-13% to-midnight to-75% px-16 py-8 shadow-xl transition-transform duration-300 lg:w-125"
        :class="isOpen ? 'translate-x-0' : 'translate-x-full'"
        :aria-hidden="!isOpen"
        data-testid="footer-locale-panel"
      >
        <div class="flex items-center justify-between md:justify-start">
          <button
            class="mr-4 p-2 text-rhino transition-colors hover:text-mouse"
            type="button"
            aria-label="Close Locale Switcher"
            data-testid="footer-locale-panel-close"
            @click="close"
          >
            <fa-icon class="size-5" :icon="faArrowRight" />
          </button>
          <p
            class="text-2xl/[1.2] max-sm:text-xl/[1.2] mb-2 font-bold uppercase"
            role="heading"
            aria-level="2"
          >
            {{ t('general/footer/block/locales/title') }}
          </p>
        </div>

        <div class="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2">
          <footer-locale-locales-list />
          <footer-locale-currencies-list @currency-selected="close" />
        </div>
      </aside>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import type { GPLocaleObject } from '~/i18n/types';

const { t, localeProperties } = useI18n();
const router = useRouter();

const currentDisplayLanguage = computed(
  () => (localeProperties.value as GPLocaleObject).displayLanguage,
);
const currencyStore = useCurrencyStore();
const currentCurrency = computed(() => currencyStore.currency);

const isOpen = ref(false);

const close = () => {
  isOpen.value = false;
};

// Lock body scroll while panel is open
const scrollLock = useScrollLock(import.meta.client ? document.body : null);
watch(isOpen, (open) => {
  scrollLock.value = open;
});

// Close on navigation
router.beforeEach(() => {
  close();
});

// Close on Escape key
onMounted(() => {
  const onEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen.value) close();
  };
  document.addEventListener('keydown', onEscape);
  onUnmounted(() => document.removeEventListener('keydown', onEscape));
});
</script>
